'use client';

import { useMemo } from 'react';
import { css, hstack } from '@bts-web/utils-style-engine';
import { decodeBase64String } from '@bts-web/utils-functions';
import { timeScalarOrNull } from '@bts-web/utils-formatting';
import { ButtonBase, CopyToClipboard } from '../../common';
import { BuySellMoreButton } from '../../transaction-details';
import { CryptoTransactionInfoStatus } from '../utils/getCryptoTransferCryptoTransferDetailsLabelsByStatus';
import { SelfDeclarationDepositDrawer } from '../../crypto-transfers/components/SelfDeclarationDepositDrawer/SelfDeclarationDepositDrawer';
import { CryptoTransfersTransactionsItemType } from '../../transaction-details/types';
import { TransferNetworkDataType } from '../../crypto-transfers/utils/useTransfersNextStepState.client';
import { getAppConfig } from '@bts-web/core-app-config';
import { useModalDrawerContext } from '../../common/components/ModalDrawerContext/ModalDrawerContext';

type CryptoTransferDetailsFooterProps = {
  cryptoTransferTransaction: CryptoTransfersTransactionsItemType;
  translations: {
    pid: string;
    orderIdCopySuccessMessage: string;
    orderIdCopyFailedMessage: string;
    copy: string;
    buyMore: string;
    verifyWallet: string;
  };
  refetchCryptoTransfersTransactions?: () => void;
};

const footerStyles = css({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 'medium',
  gap: 'medium',
});

const CryptoTransferDetailsFooter = ({
  translations,
  cryptoTransferTransaction,
  refetchCryptoTransfersTransactions,
}: CryptoTransferDetailsFooterProps) => {
  const { openDrawer } = useModalDrawerContext();

  const { appId } = getAppConfig();

  const {
    asset,
    network,
    status,
    externalAddress,
    createdAt,
    assetAmount,
    requiredActions,
  } = cryptoTransferTransaction || {};

  const decodedTransactionId =
    cryptoTransferTransaction?.id &&
    decodeBase64String(cryptoTransferTransaction.id).split(':')[1];

  const pid = cryptoTransferTransaction?.id && (
    <div
      className={hstack({
        overflow: 'hidden',
        fontSize: 'label.medium_medium',
        justifyContent: 'space-between',
        flex: 'auto',
        width: '100%',
      })}
    >
      <div
        role="presentation"
        className={css({
          flex: 1,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          minWidth: 0,
          color: 'neutrals.text_secondary',
          fontSize: 'caption.small',
          fontWeight: 'caption.small',
          lineHeight: 'caption.small',
          letterSpacing: 'caption.small',
        })}
      >
        {translations.pid} {decodedTransactionId}
      </div>

      <CopyToClipboard
        value={decodedTransactionId as string}
        translations={{
          successMessage: translations.orderIdCopySuccessMessage,
          failedMessage: translations.orderIdCopyFailedMessage,
        }}
      />
    </div>
  );

  const handleVerifyWallet = () => {
    if (asset?.id && externalAddress?.id) {
      openDrawer(SelfDeclarationDepositDrawer, {
        cryptoTransferData: {
          assetData: asset,
          networkData: network as TransferNetworkDataType,
          externalAddress,
          createdAt: timeScalarOrNull(createdAt),
          assetAmount,
        },
        refetchCryptoTransfersTransactions,
      });
    }
  };

  const beneficiaryActionsRequired = (requiredActions || []).some(
    (str) => str && str.includes('BENEFICIARY_DETAILS'),
  );

  const actionButton = useMemo(() => {
    if (
      status === CryptoTransactionInfoStatus.ON_HOLD &&
      beneficiaryActionsRequired
    ) {
      return (
        <ButtonBase
          onClick={handleVerifyWallet}
          size="large"
          visual={appId === 4 ? 'accent' : 'primary'}
        >
          {translations.verifyWallet}
        </ButtonBase>
      );
    }

    if (asset?.id) {
      return (
        <BuySellMoreButton isSell={false} assetId={asset.id}>
          {translations.buyMore}
        </BuySellMoreButton>
      );
    }

    return null;
  }, [asset, status, beneficiaryActionsRequired]);

  return (
    <div
      className={css({ width: '100%', marginTop: 'auto' })}
      aria-label="crypto-transfer-details-footer"
    >
      <footer className={footerStyles}>
        {pid}

        {actionButton}
      </footer>
    </div>
  );
};

export { CryptoTransferDetailsFooter };
